// Programa anual (lote C, M48): un solo panel para los cinco programas (capacitación TH 8, exámenes PO 2, psicosocial
// PO 24 y 25, alcohol, tabaco y otras drogas PO 26 y 27, inspecciones internas PO 14, con el cronograma armado desde las
// programaciones de Inspecciones v2). Pasos: contenido y cronograma (borrador) → preparar para
// firma (contenido congelado con SHA-256) → firmar en pantalla (responsable de SSO y representante legal, con el
// dedo, a 360 px) o imprimir para firma manual y subir el escaneo → aprobar (queda inmutable; para cambiar algo se
// crea una versión nueva) → ejecución de cada actividad con fecha real y evidencia o registro vinculado → informe.
// Reutiliza SignaturePad (permisos de trabajo, plan, inspecciones y firmas del trabajador).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad, { type SignaturePadHandle } from '../permits/SignaturePad';
import {
  ACEPTA_ARCHIVO, actividadesDe, agregarActividades, aprobarVersion, borrarActividades, borrarBorrador, borrarEjecucion, cargarOrigenes,
  cargarPrograma, crearCapacitacionDeActividad, crearPrograma, crearVersionNueva, dataUrlFirma, editarActividad, firmarVersion,
  guardarCabecera, guardarRespaldosVinculados, guardarSut, prepararVersion, registrarEjecucion, subirEscaneo, urlArchivo, volverABorrador,
  type DatosPrograma, type Origenes,
} from '../../lib/programas/prog-datos';
import { evidenciaPsicosocialDelAnio } from '../../lib/programas/prog-checklist';
import {
  AVISO_LISTA_PSICOSOCIAL, cedulaValida, contenidoVersion, cronogramaExamenes, cronogramaInspecciones, EJEMPLO_INDICADOR, estadoActividad,
  ETIQUETA_ESTADO_ACTIVIDAD, faltantesParaAprobar, faltantesParaPreparar, fechaCorta, isoLocal, MOTIVO_MINIMO, respaldos, respaldosPorGuardar,
  resultadoPrograma, resultadoSut, textoCapacitacion, textoDestinatarios, textoExamen, type EstadoActividad,
} from '../../lib/programas/prog-logica';
import { fechaHora, generarInformeEjecucionPDF, generarProgramaPDF, nombreArchivoPrograma } from '../../lib/programas/prog-pdf';
import {
  CITA_MATRIZ_SUSTANCIAS, CONFIG, ETIQUETA_DESTINATARIOS, ETIQUETA_ESTADO_ITEM, ETIQUETA_ESTADO_VERSION, ETIQUETA_EXAMEN, ETIQUETA_ROL, NOTA_PERIODICIDAD,
  NOTA_PLAZOS, PERIODICIDAD_EXAMENES_DEFECTO, ROLES_FIRMA, TEXTO_FIRMA_PROGRAMA, TIPOS_EXAMEN,
  type ActividadEditable, type Destinatarios, type FilaActividad, type FilaEjecucion, type FilaVersion, type MetodoFirma, type RolFirmaPrograma,
  type TipoExamen, type TipoPrograma,
} from '../../lib/programas/prog-tipos';
import { plural } from '../../lib/plurales';

export interface ProgramaAnualPanelProps {
  tipo: TipoPrograma;
  companyId: string;
  empresa: string;
  puedeEditar: boolean;   // admin o manager (o superadmin impersonando)
  /** Se llama después de crear una capacitación planificada desde una actividad (la tabla de Capacitaciones se recarga). */
  onCapacitacionCreada?: () => void | Promise<void>;
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

const S = {
  caja: { background: '#1e293b', border: '1px solid #334155', borderRadius: 14, boxSizing: 'border-box', width: '100%' } as React.CSSProperties,
  cabecera: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, padding: '14px 14px 10px', flexWrap: 'wrap' } as React.CSSProperties,
  cuerpo: { padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 12 } as React.CSSProperties,
  bloque: { background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 } as React.CSSProperties,
  h3: { color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 700, margin: 0 } as React.CSSProperties,
  nota: { color: '#94a3b8', fontSize: '0.78rem', margin: 0, lineHeight: 1.45 } as React.CSSProperties,
  botones: { display: 'flex', gap: 8, flexWrap: 'wrap' } as React.CSSProperties,
  input: { width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', padding: '9px 10px', fontSize: '0.88rem' } as React.CSSProperties,
  etiqueta: { color: '#94a3b8', fontSize: '0.75rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: 4 } as React.CSSProperties,
  tarjeta: { border: '1px solid #334155', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 } as React.CSSProperties,
};
const boton = (tipo: 'primario' | 'secundario' | 'peligro' = 'secundario', deshabilitado = false): React.CSSProperties => ({
  padding: '9px 14px', borderRadius: 8, fontWeight: 600, fontSize: '0.84rem', cursor: deshabilitado ? 'not-allowed' : 'pointer', opacity: deshabilitado ? 0.55 : 1,
  minHeight: 40,
  ...(tipo === 'primario' ? { background: '#2FBF71', border: '1px solid #2FBF71', color: '#0f172a' }
    : tipo === 'peligro' ? { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171' }
      : { background: 'transparent', border: '1px solid #475569', color: '#cbd5e1' }),
});
const COLOR_ESTADO: Record<string, string> = { cumple: '#2FBF71', en_proceso: '#f59e0b', no_cumple: '#ef4444', pendiente: '#94a3b8' };
const COLOR_ACTIVIDAD: Record<EstadoActividad, string> = { ejecutada: '#2FBF71', por_venir: '#94a3b8', vencida: '#f59e0b', vencida_grave: '#ef4444' };

function abrirConUrl(promesa: Promise<string>) {
  // La pestaña se abre en el mismo clic (Safari bloquea window.open después de una espera).
  const v = window.open('', '_blank');
  if (v) v.opener = null;
  promesa.then(url => { if (v) v.location.href = url; else window.open(url, '_blank', 'noopener,noreferrer'); })
    .catch(e => { v?.close(); alert(msg(e)); });
}

const vacia = (tipo: TipoPrograma, anio: number): ActividadEditable => ({
  title: '', audience_kind: tipo === 'examenes' ? 'trabajador' : 'todos', audience_label: null, position_id: null, worker_id: null,
  planned_date: `${anio}-01-31`, responsible: null, duration_hours: null, training_id: null, exam_type: tipo === 'examenes' ? 'periodico' : null,
  notes: null, template_no: null, indicator: null, schedule_id: null,
});

export default function ProgramaAnualPanel({ tipo, companyId, empresa, puedeEditar, onCapacitacionCreada }: ProgramaAnualPanelProps) {
  const cfg = CONFIG[tipo];
  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState<DatosPrograma | null>(null);
  const [origenes, setOrigenes] = useState<Origenes>({ trabajadores: [], puestos: [], capacitaciones: [], examenes: [] });
  const [evPsico, setEvPsico] = useState<Record<number, string>>({});
  const [cargando, setCargando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [cabecera, setCabecera] = useState({ objectives: '', scope_diagnosis: '', methodology: '', responsibles: '', resources: '', periodicidad: String(PERIODICIDAD_EXAMENES_DEFECTO) });
  const [editando, setEditando] = useState<{ id: string | null; a: ActividadEditable } | null>(null);
  const [metodo, setMetodo] = useState<MetodoFirma>('pantalla');
  const [firmando, setFirmando] = useState<RolFirmaPrograma | null>(null);
  const [firmante, setFirmante] = useState({ nombre: '', cedula: '' });
  const [motivo, setMotivo] = useState('');
  const [nuevaVersion, setNuevaVersion] = useState(false);
  const [ejecutando, setEjecutando] = useState<FilaActividad | null>(null);
  const [sut, setSut] = useState({ codigo: '', fecha: '' });
  const pad = useRef<SignaturePadHandle>(null);
  const guardando = useRef(false);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setCargando(true); setError('');
    try {
      const [d, o, ev] = await Promise.all([
        cargarPrograma(companyId, tipo, anio), cargarOrigenes(companyId, tipo),
        tipo === 'psicosocial' ? evidenciaPsicosocialDelAnio(companyId, anio).catch(() => ({})) : Promise.resolve({}),
      ]);
      setDatos(d); setOrigenes(o); setEvPsico(ev);
      const v = d.versiones.find(x => x.status === 'borrador' || x.status === 'en_firma') ?? d.versiones.find(x => x.status === 'aprobada');
      if (v) {
        setCabecera({ objectives: v.objectives ?? '', scope_diagnosis: v.scope_diagnosis ?? '', methodology: v.methodology ?? '',
          responsibles: v.responsibles ?? '', resources: v.resources ?? '', periodicidad: String(v.settings?.periodicidad_meses ?? PERIODICIDAD_EXAMENES_DEFECTO) });
        if (v.sign_method) setMetodo(v.sign_method);
      }
      setSut({ codigo: d.sut?.registration_code ?? '', fecha: d.sut?.registration_date ?? '' });
    } catch (e) { setError(msg(e)); } finally { setCargando(false); }
  }, [companyId, tipo, anio]);
  useEffect(() => { if (abierto) cargar(); }, [abierto, cargar]);

  const ejecutar = async (fn: () => Promise<void>, exito?: string, recargar = true) => {
    setOcupado(true); setError(''); setOk('');
    try { await fn(); if (exito) setOk(exito); if (recargar) await cargar(); } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  };

  // ── Derivados ──
  const programa = datos?.programa ?? null;
  const versiones = datos?.versiones ?? [];
  const trabajo = versiones.find(x => x.status === 'borrador' || x.status === 'en_firma') ?? null;
  const aprobada = versiones.find(x => x.status === 'aprobada') ?? null;
  const visible: FilaVersion | null = trabajo ?? aprobada;
  const actividadesVisible = useMemo(() => actividadesDe(datos?.actividades ?? [], visible?.id), [datos, visible]);
  const actividadesAprobada = useMemo(() => actividadesDe(datos?.actividades ?? [], aprobada?.id), [datos, aprobada]);
  const firmasDe = (v: FilaVersion | null) => (v ? (datos?.firmas ?? []).filter(f => f.version_id === v.id) : []);
  const mapaRespaldos = useMemo(() => respaldos(tipo, anio, actividadesAprobada, {
    ejecuciones: datos?.ejecuciones ?? [], capacitaciones: origenes.capacitaciones, examenes: origenes.examenes, evidenciaPsicosocial: evPsico,
    inspecciones: origenes.inspecciones,
  }), [tipo, anio, actividadesAprobada, datos, origenes, evPsico]);
  const hoyIso = isoLocal(new Date());
  const resultado = useMemo(() => (anio === anioActual ? resultadoPrograma({
    tipo, programa, hayVersion: versiones.length > 0, aprobada, actividades: actividadesAprobada, respaldos: mapaRespaldos,
    empresaRegistrada: origenes.empresaRegistrada,
  }) : null), [anio, anioActual, tipo, programa, versiones.length, aprobada, actividadesAprobada, mapaRespaldos, origenes.empresaRegistrada]);
  const resultadoSutAnio = useMemo(() => ((tipo === 'psicosocial' || tipo === 'sustancias') && anio === anioActual
    ? resultadoSut(tipo, programa, datos?.sut ?? null) : null), [tipo, anio, anioActual, programa, datos]);

  // Los respaldos que vienen de una capacitación realizada o de un examen se guardan para conservarlos.
  useEffect(() => {
    if (!puedeEditar || !programa || !aprobada || guardando.current) return;
    const lista = respaldosPorGuardar(mapaRespaldos);
    if (!lista.length) return;
    guardando.current = true;
    guardarRespaldosVinculados(programa, lista).then(hecho => { if (hecho) cargar(); }).finally(() => { guardando.current = false; });
  }, [puedeEditar, programa, aprobada, mapaRespaldos, cargar]);

  const enBorrador = visible?.status === 'borrador';
  const enFirma = visible?.status === 'en_firma';
  const editable = puedeEditar && enBorrador;
  const faltan = visible && enBorrador ? faltantesParaPreparar(tipo, { objectives: cabecera.objectives, scope_diagnosis: cabecera.scope_diagnosis,
    methodology: cabecera.methodology, responsibles: cabecera.responsibles, resources: cabecera.resources }, actividadesVisible) : [];
  const cabeceraCambiada = !!visible && enBorrador && (
    (visible.objectives ?? '') !== cabecera.objectives || (visible.scope_diagnosis ?? '') !== cabecera.scope_diagnosis
    || (visible.methodology ?? '') !== cabecera.methodology || (visible.responsibles ?? '') !== cabecera.responsibles
    || (visible.resources ?? '') !== cabecera.resources
    || (tipo === 'examenes' && String(visible.settings?.periodicidad_meses ?? PERIODICIDAD_EXAMENES_DEFECTO) !== cabecera.periodicidad));

  // ── Acciones ──
  const crear = () => ejecutar(() => crearPrograma(companyId, tipo, anio), `Programa ${anio} creado como borrador.`);
  const camposCabecera = () => {
    const meses = Math.round(Number(cabecera.periodicidad));
    if (tipo === 'examenes' && (!Number.isFinite(meses) || meses < 1 || meses > 60)) throw new Error('La periodicidad debe estar entre 1 y 60 meses.');
    return { objectives: cabecera.objectives, scope_diagnosis: cabecera.scope_diagnosis, methodology: cabecera.methodology,
      responsibles: cabecera.responsibles, resources: cabecera.resources, ...(tipo === 'examenes' ? { settings: { periodicidad_meses: meses } } : {}) };
  };
  const guardarCab = () => ejecutar(async () => { await guardarCabecera(visible!, camposCabecera()); }, 'Contenido guardado.');

  const guardarActividad = () => ejecutar(async () => {
    if (!editando || !visible || !programa) return;
    const a = editando.a;
    if (a.title.trim().length < 3) throw new Error('Escriba la actividad (al menos 3 caracteres).');
    if (!a.planned_date) throw new Error('Indique la fecha planificada.');
    const puesto = origenes.puestos.find(p => p.id === a.position_id);
    const trabajador = origenes.trabajadores.find(w => w.id === a.worker_id);
    const fila: ActividadEditable = {
      ...a, title: a.title.trim(), responsible: a.responsible?.trim() || null, notes: a.notes?.trim() || null,
      position_id: a.audience_kind === 'puesto' ? a.position_id : null, worker_id: a.audience_kind === 'trabajador' ? a.worker_id : null,
      audience_label: a.audience_kind === 'puesto' ? (puesto?.name ?? a.audience_label) : a.audience_kind === 'trabajador'
        ? (trabajador ? (trabajador.position ? `${trabajador.full_name} (${trabajador.position})` : trabajador.full_name) : a.audience_label) : null,
    };
    if (editando.id) await editarActividad(editando.id, fila);
    else await agregarActividades(companyId, programa.id, visible.id, [fila], actividadesVisible.length);
    setEditando(null);
  }, 'Actividad guardada.');

  const quitarActividad = (a: FilaActividad) => {
    if (!window.confirm(`¿Quitar "${a.title}" del cronograma?`)) return;
    ejecutar(() => borrarActividades([a.id]), 'Actividad quitada.');
  };

  const armarExamenes = () => {
    if (!visible || !programa) return;
    const meses = Math.round(Number(cabecera.periodicidad)) || PERIODICIDAD_EXAMENES_DEFECTO;
    const lista = cronogramaExamenes(anio, origenes.trabajadores, origenes.examenes, meses);
    if (!lista.length) { setError('No hay trabajadores para armar el cronograma: regístrelos en Trabajadores.'); return; }
    const aviso = actividadesVisible.length
      ? `Se reemplaza el cronograma del borrador (${plural(actividadesVisible.length, 'actividad', 'actividades')}) por ${plural(lista.length, 'examen calculado', 'exámenes calculados')}. ¿Continuar?`
      : `Se agregan ${plural(lista.length, 'examen calculado', 'exámenes calculados')} desde Trabajadores. ¿Continuar?`;
    if (!window.confirm(aviso)) return;
    ejecutar(async () => {
      if (cabeceraCambiada) await guardarCabecera(visible, camposCabecera());
      await borrarActividades(actividadesVisible.map(a => a.id));
      await agregarActividades(companyId, programa.id, visible.id, lista);
    }, 'Cronograma armado desde Trabajadores y su último examen. Revise las fechas antes de preparar.');
  };

  // PO 14: una actividad por cada fecha del año de cada programación activa de Inspecciones v2.
  const armarInspecciones = () => {
    if (!visible || !programa) return;
    const lista = cronogramaInspecciones(anio, origenes.programaciones ?? [], origenes.nombres ?? { plantillas: {}, sedes: {}, trabajadores: {}, varias: false });
    if (!lista.length) { setError(`No hay inspecciones programadas para ${anio}: prográmelas arriba, en "Nueva inspección" (Programar).`); return; }
    const aviso = actividadesVisible.length
      ? `Se reemplaza el cronograma del borrador (${plural(actividadesVisible.length, 'actividad', 'actividades')}) por ${plural(lista.length, 'inspección programada', 'inspecciones programadas')}. ¿Continuar?`
      : `Se agregan ${plural(lista.length, 'inspección programada', 'inspecciones programadas')} desde las programaciones. ¿Continuar?`;
    if (!window.confirm(aviso)) return;
    ejecutar(async () => {
      if (cabeceraCambiada) await guardarCabecera(visible, camposCabecera());
      await borrarActividades(actividadesVisible.map(a => a.id));
      await agregarActividades(companyId, programa.id, visible.id, lista);
    }, 'Cronograma armado desde las programaciones de Inspecciones v2. Cada inspección firmada ejecutará su actividad.');
  };

  const crearCapacitacion = (a: FilaActividad) => ejecutar(async () => { await crearCapacitacionDeActividad(companyId, a); await onCapacitacionCreada?.(); },
    'Capacitación planificada creada en Capacitaciones y vinculada a la actividad.');

  const preparar = () => ejecutar(async () => {
    if (!visible || !programa) return;
    if (cabeceraCambiada) await guardarCabecera(visible, camposCabecera());
    if (faltan.length) throw new Error('Complete lo que falta antes de preparar el programa.');
    const v = { ...visible, ...camposCabecera(), settings: tipo === 'examenes' ? { periodicidad_meses: Math.round(Number(cabecera.periodicidad)) } : visible.settings };
    await prepararVersion(tipo, empresa, programa, v, actividadesVisible);
  }, 'Programa preparado: el contenido quedó congelado con su huella. Ya se puede firmar.');

  const editar = () => ejecutar(() => volverABorrador(visible!), 'Puede volver a editar el programa.');

  const guardarFirma = () => ejecutar(async () => {
    if (!visible || !programa || !firmando) return;
    if (firmante.nombre.trim().length < 3) throw new Error('Escriba el nombre de quien firma.');
    if (!cedulaValida(firmante.cedula)) throw new Error('La cédula debe tener 10 dígitos.');
    const png = await pad.current?.toBlob();
    if (!png || pad.current?.isEmpty()) throw new Error('Dibuje la firma en el recuadro.');
    await firmarVersion(programa, visible, { rol: firmando, nombre: firmante.nombre, cedula: firmante.cedula }, png);
    setFirmando(null); setFirmante({ nombre: '', cedula: '' });
  }, 'Firma guardada con la fecha y hora del servidor.');

  const subirEsc = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo || !visible || !programa) return;
    ejecutar(() => subirEscaneo(programa, visible, archivo), 'Escaneo del programa firmado subido.');
  };

  const aprobar = () => ejecutar(() => aprobarVersion(visible!, metodo), 'Programa aprobado: esta versión queda inmutable.');

  const versionNueva = () => ejecutar(async () => {
    if (!programa || !aprobada) return;
    if (motivo.trim().length < MOTIVO_MINIMO) throw new Error(`Escriba el motivo del cambio (al menos ${MOTIVO_MINIMO} caracteres).`);
    const numero = Math.max(...versiones.map(v => v.version)) + 1;
    await crearVersionNueva(programa, aprobada, actividadesAprobada, numero, motivo);
    setNuevaVersion(false); setMotivo('');
  }, 'Versión nueva creada como borrador. La versión aprobada sigue vigente hasta que se apruebe la nueva.');

  const descartarBorrador = () => {
    if (!visible || !window.confirm(`¿Borrar el borrador de la versión ${visible.version}?`)) return;
    ejecutar(() => borrarBorrador(visible), 'Borrador borrado.');
  };

  const pdfPrograma = (v: FilaVersion, blanco: boolean) => ejecutar(async () => {
    const contenido = v.snapshot ?? contenidoVersion(tipo, empresa, anio, v, actividadesDe(datos?.actividades ?? [], v.id));
    const firmas = blanco ? [] : await Promise.all(firmasDe(v).map(async f => ({ rol: f.role, nombre: f.signer_name, cedula: f.signer_cedula,
      firmadoEn: f.signed_at, hash: f.content_hash, imagen: await dataUrlFirma(f.image_path) })));
    generarProgramaPDF({ contenido, hash: v.content_hash, estado: v.status, metodo: v.sign_method, aprobadoEn: v.approved_at, firmas, blanco, fecha: new Date() })
      .save(nombreArchivoPrograma(contenido, blanco ? 'para_firmar' : v.status === 'aprobada' ? 'aprobado' : v.status));
  }, undefined, false);

  const pdfInforme = () => ejecutar(async () => {
    if (!aprobada) return;
    const contenido = aprobada.snapshot ?? contenidoVersion(tipo, empresa, anio, aprobada, actividadesAprobada);
    const res = resultado ?? resultadoPrograma({ tipo, programa, hayVersion: true, aprobada, actividades: actividadesAprobada, respaldos: mapaRespaldos,
      empresaRegistrada: origenes.empresaRegistrada },
      new Date(Date.UTC(anio, 11, 31, 12)));
    generarInformeEjecucionPDF({
      contenido, aprobadoEn: aprobada.approved_at, resultado: res, fecha: new Date(),
      filas: actividadesAprobada.map(a => {
        const r = mapaRespaldos.get(a.activity_key);
        return { actividad: a.title, numero: a.template_no, destinatarios: textoDestinatarios(a), planificada: a.planned_date,
          estado: estadoActividad(a.planned_date, r, hoyIso), ejecutada: r?.fecha ?? null, respaldo: r?.texto ?? '' };
      }),
    }).save(nombreArchivoPrograma(contenido, 'informe_ejecucion'));
  }, undefined, false);

  const guardarSutUI = (archivo: File | null) => ejecutar(async () => {
    if (!programa) return;
    if (!sut.codigo.trim()) throw new Error('Escriba el número o código del registro en el SUT.');
    if (!sut.fecha) throw new Error('Indique la fecha del registro.');
    await guardarSut(programa, datos?.sut ?? null, sut.codigo, sut.fecha, archivo);
  }, 'Registro en el SUT guardado.');

  // ── Render ──
  const estadoCab = resultado ?? null;
  return (
    <section style={S.caja} aria-label={cfg.titulo}>
      <div style={S.cabecera}>
        <div style={{ minWidth: 0, flex: '1 1 220px' }}>
          <h3 style={{ ...S.h3, fontSize: '1rem' }}>{cfg.titulo}</h3>
          <p style={{ ...S.nota, marginTop: 4 }}>{cfg.norma}</p>
          {estadoCab && abierto && (
            <p style={{ ...S.nota, marginTop: 6 }}>
              <b style={{ color: COLOR_ESTADO[estadoCab.estado] }}>{ETIQUETA_ESTADO_ITEM[estadoCab.estado]}</b> · {estadoCab.resumen}
            </p>
          )}
        </div>
        <div style={S.botones}>
          {abierto && (
            <select aria-label="Año del programa" value={anio} onChange={e => setAnio(Number(e.target.value))} style={{ ...S.input, width: 'auto', minHeight: 40 }}>
              {[anioActual - 1, anioActual, anioActual + 1].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
          <button type="button" style={boton('secundario')} onClick={() => setAbierto(x => !x)} aria-expanded={abierto}>
            {abierto ? 'Ocultar' : 'Abrir programa anual'}
          </button>
        </div>
      </div>

      {abierto && (
        <div style={S.cuerpo}>
          {error && <p role="alert" style={{ ...S.nota, color: '#f87171' }}>{error}</p>}
          {ok && <p role="status" style={{ ...S.nota, color: '#4ade80' }}>{ok}</p>}
          {cargando && !datos && <p style={S.nota}>Cargando…</p>}

          {datos && !programa && (
            <div style={S.bloque}>
              <h4 style={S.h3}>No hay programa {anio}</h4>
              <p style={S.nota}>Contenido mínimo: {cfg.contenidoMinimo}</p>
              {puedeEditar ? (
                <div style={S.botones}><button type="button" style={boton('primario', ocupado)} disabled={ocupado} onClick={crear}>Crear programa {anio}</button></div>
              ) : <p style={S.nota}>Solo el administrador o el manager crean el programa.</p>}
            </div>
          )}

          {programa && visible && (
            <>
              {/* Versiones */}
              <div style={S.bloque}>
                <h4 style={S.h3}>Versiones</h4>
                <ul style={{ margin: 0, paddingLeft: 18, color: '#cbd5e1', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {versiones.map(v => (
                    <li key={v.id}>
                      Versión {v.version}: <b>{ETIQUETA_ESTADO_VERSION[v.status]}</b>
                      {v.approved_at ? ` el ${fechaHora(v.approved_at)}` : ''}{v.change_reason ? ` · ${v.change_reason}` : ''}{' '}
                      {(v.status === 'aprobada' || v.status === 'reemplazada') && (
                        <button type="button" style={{ ...boton('secundario', ocupado), padding: '4px 10px', minHeight: 32 }} disabled={ocupado} onClick={() => pdfPrograma(v, false)}>PDF</button>
                      )}
                    </li>
                  ))}
                </ul>
                {aprobada && !trabajo && puedeEditar && !nuevaVersion && (
                  <div style={S.botones}><button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => setNuevaVersion(true)}>Crear versión nueva</button></div>
                )}
                {nuevaVersion && (
                  <>
                    <label style={S.etiqueta}>Motivo del cambio
                      <textarea style={{ ...S.input, minHeight: 60 }} value={motivo} onChange={e => setMotivo(e.target.value)} maxLength={1000} />
                    </label>
                    <div style={S.botones}>
                      <button type="button" style={boton('primario', ocupado)} disabled={ocupado} onClick={versionNueva}>Crear borrador de la versión nueva</button>
                      <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => setNuevaVersion(false)}>Cancelar</button>
                    </div>
                  </>
                )}
                {visible.status === 'aprobada' && <p style={S.nota}>La versión aprobada no se edita: para cambiar algo, cree una versión nueva.</p>}
              </div>

              {/* Contenido */}
              <div style={S.bloque}>
                <h4 style={S.h3}>Contenido · versión {visible.version} ({ETIQUETA_ESTADO_VERSION[visible.status]})</h4>
                <p style={S.nota}>Contenido mínimo: {cfg.contenidoMinimo}</p>
                {([['objectives', 'Objetivos'], ['scope_diagnosis', tipo === 'capacitacion' ? 'Alcance y diagnóstico de necesidades'
                  : tipo === 'inspecciones' ? 'Alcance y áreas a inspeccionar' : 'Alcance y diagnóstico'],
                  ['methodology', tipo === 'capacitacion' ? 'Metodología (talleres, clases, simulacros, prácticas)'
                    : tipo === 'inspecciones' ? 'Lista de verificación (plantillas que se usan)' : 'Metodología'],
                  ['responsibles', 'Responsables'], ['resources', tipo === 'capacitacion' ? 'Recursos y material utilizado' : 'Recursos y material']] as [keyof typeof cabecera, string][])
                  .map(([k, t]) => (
                    <label key={k} style={S.etiqueta}>{t}
                      {editable
                        ? <textarea style={{ ...S.input, minHeight: 56 }} value={cabecera[k]} maxLength={4000} onChange={e => setCabecera(c => ({ ...c, [k]: e.target.value }))} />
                        : <span style={{ color: '#e2e8f0', fontWeight: 400, fontSize: '0.84rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{cabecera[k] || '—'}</span>}
                    </label>
                  ))}
                {tipo === 'examenes' && (
                  <>
                    <label style={S.etiqueta}>Periodicidad de los exámenes periódicos (meses)
                      {editable
                        ? <input type="number" min={1} max={60} inputMode="numeric" style={{ ...S.input, maxWidth: 140 }} value={cabecera.periodicidad}
                          onChange={e => setCabecera(c => ({ ...c, periodicidad: e.target.value }))} />
                        : <span style={{ color: '#e2e8f0', fontWeight: 400 }}>{cabecera.periodicidad} meses</span>}
                    </label>
                    <p style={S.nota}>{NOTA_PERIODICIDAD}</p>
                  </>
                )}
                {editable && cabeceraCambiada && (
                  <div style={S.botones}><button type="button" style={boton('primario', ocupado)} disabled={ocupado} onClick={guardarCab}>Guardar contenido</button></div>
                )}
              </div>

              {/* Cronograma */}
              <div style={S.bloque}>
                <h4 style={S.h3}>{tipo === 'examenes' ? 'Cronograma de exámenes' : 'Cronograma de actividades'} ({actividadesVisible.length})</h4>
                {tipo === 'psicosocial' && <p style={S.nota}>{AVISO_LISTA_PSICOSOCIAL}</p>}
                {tipo === 'sustancias' && <p style={S.nota}>Las 14 actividades: {CITA_MATRIZ_SUSTANCIAS}. Ajuste las fechas, los responsables y el indicador de cada una.</p>}
                {tipo === 'inspecciones' && <p style={S.nota}>El cronograma se arma desde las programaciones de Inspecciones v2 (plantilla, área o sede y frecuencia). Cada inspección programada que se firma deja su actividad ejecutada.</p>}
                {editable && (
                  <div style={S.botones}>
                    <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => setEditando({ id: null, a: vacia(tipo, anio) })}>+ Agregar actividad</button>
                    {tipo === 'examenes' && <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={armarExamenes}>Armar desde Trabajadores</button>}
                    {tipo === 'inspecciones' && <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={armarInspecciones}>Armar desde las programaciones</button>}
                  </div>
                )}
                {editando && (
                  <FormActividad tipo={tipo} valor={editando.a} origenes={origenes} ocupado={ocupado}
                    onCambio={a => setEditando(x => (x ? { ...x, a } : x))} onGuardar={guardarActividad} onCancelar={() => setEditando(null)} />
                )}
                {actividadesVisible.map(a => {
                  const cap = a.training_id ? origenes.capacitaciones.find(c => c.id === a.training_id) : null;
                  return (
                    <div key={a.id} style={S.tarjeta}>
                      <div style={{ color: '#e2e8f0', fontSize: '0.86rem', fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {a.template_no ? `${a.template_no}. ` : ''}{a.title}
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '0.78rem', display: 'flex', gap: '4px 12px', flexWrap: 'wrap' }}>
                        {tipo !== 'inspecciones' && <span>{textoDestinatarios(a)}</span>}
                        <span>Fecha: {fechaCorta(a.planned_date)}</span>
                        {a.indicator && <span>Indicador: {a.indicator}</span>}
                        {a.exam_type && <span>Examen: {ETIQUETA_EXAMEN[a.exam_type]}</span>}
                        {a.responsible && <span>Responsable: {a.responsible}</span>}
                        {a.duration_hours !== null && <span>Duración: {String(a.duration_hours).replace('.', ',')} h</span>}
                        {tipo === 'capacitacion' && <span>{cap ? `Capacitación: ${cap.title} (${cap.status === 'realizada' ? 'realizada' : 'planificada'})` : a.training_id ? 'Capacitación borrada' : 'Sin capacitación vinculada'}</span>}
                      </div>
                      {a.notes && <p style={{ ...S.nota, fontSize: '0.74rem' }}>{a.notes}</p>}
                      {editable && (
                        <div style={S.botones}>
                          <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => setEditando({ id: a.id, a: { ...a } })}>Editar</button>
                          {tipo === 'capacitacion' && !a.training_id && (
                            <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => crearCapacitacion(a)}>Crear capacitación planificada</button>
                          )}
                          <button type="button" style={boton('peligro', ocupado)} disabled={ocupado} onClick={() => quitarActividad(a)}>Quitar</button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!actividadesVisible.length && <p style={S.nota}>Sin actividades.</p>}
              </div>

              {/* Aprobación */}
              <div style={S.bloque}>
                <h4 style={S.h3}>Aprobación y firmas</h4>
                <p style={S.nota}>Firman el responsable de seguridad y salud en el trabajo y el representante legal. {TEXTO_FIRMA_PROGRAMA}</p>
                {enBorrador && (
                  <>
                    {faltan.length > 0 && <ul style={{ margin: 0, paddingLeft: 18, color: '#fbbf24', fontSize: '0.8rem' }}>{faltan.map(f => <li key={f}>{f}</li>)}</ul>}
                    <div style={S.botones}>
                      {puedeEditar && <button type="button" style={boton('primario', ocupado || faltan.length > 0)} disabled={ocupado || faltan.length > 0} onClick={preparar}>Preparar para firma</button>}
                      <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => pdfPrograma(visible, false)}>Vista previa (PDF)</button>
                      {puedeEditar && visible.version > 1 && <button type="button" style={boton('peligro', ocupado)} disabled={ocupado} onClick={descartarBorrador}>Borrar borrador</button>}
                    </div>
                  </>
                )}
                {enFirma && (
                  <>
                    {visible.content_hash && <p style={{ ...S.nota, fontSize: '0.7rem', overflowWrap: 'anywhere' }}>Huella del contenido (SHA-256): {visible.content_hash}</p>}
                    <div role="radiogroup" aria-label="Cómo se firma" style={S.botones}>
                      {(['pantalla', 'papel'] as MetodoFirma[]).map(m => (
                        <button key={m} type="button" role="radio" aria-checked={metodo === m} style={boton(metodo === m ? 'primario' : 'secundario', !puedeEditar)}
                          disabled={!puedeEditar} onClick={() => setMetodo(m)}>{m === 'pantalla' ? 'Firmar en pantalla' : 'Firmar en papel'}</button>
                      ))}
                    </div>
                    {metodo === 'pantalla' ? ROLES_FIRMA.map(rol => {
                      const f = firmasDe(visible).find(x => x.role === rol);
                      return (
                        <div key={rol} style={S.tarjeta}>
                          <b style={{ color: '#e2e8f0', fontSize: '0.84rem' }}>{ETIQUETA_ROL[rol]}</b>
                          {f ? <span style={S.nota}>Firmó {f.signer_name} (C.I. {f.signer_cedula}) el {fechaHora(f.signed_at)}.</span>
                            : firmando === rol ? (
                              <>
                                <label style={S.etiqueta}>Nombre completo<input style={S.input} value={firmante.nombre} maxLength={160} onChange={e => setFirmante(x => ({ ...x, nombre: e.target.value }))} /></label>
                                <label style={S.etiqueta}>Cédula<input style={S.input} inputMode="numeric" value={firmante.cedula} maxLength={10} onChange={e => setFirmante(x => ({ ...x, cedula: e.target.value.replace(/\D/g, '') }))} /></label>
                                {/* El pad trae su propio botón "Borrar firma": no se repite aquí. */}
                                <SignaturePad ref={pad} height={170} disabled={ocupado} />
                                <div style={S.botones}>
                                  <button type="button" style={boton('primario', ocupado)} disabled={ocupado} onClick={guardarFirma}>Guardar firma</button>
                                  <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => setFirmando(null)}>Cancelar</button>
                                </div>
                              </>
                            ) : puedeEditar ? (
                              <div style={S.botones}><button type="button" style={boton('secundario', ocupado || !!firmando)} disabled={ocupado || !!firmando} onClick={() => { setFirmando(rol); setFirmante({ nombre: '', cedula: '' }); }}>Firmar</button></div>
                            ) : <span style={S.nota}>Sin firma.</span>}
                        </div>
                      );
                    }) : (
                      <div style={S.tarjeta}>
                        <p style={S.nota}>Imprima el formato, recoja las firmas a mano y suba el escaneo firmado (PDF, JPG o PNG, hasta 10 MB).</p>
                        <div style={S.botones}>
                          <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => pdfPrograma(visible, true)}>Imprimir para firma manual</button>
                          {visible.scan_path
                            ? <button type="button" style={boton('secundario')} onClick={() => abrirConUrl(urlArchivo(visible.scan_path!))}>Ver escaneo</button>
                            : puedeEditar && (
                              <label style={{ ...boton('secundario', ocupado), display: 'inline-flex', alignItems: 'center' }}>
                                Subir escaneo firmado
                                <input type="file" accept={ACEPTA_ARCHIVO} onChange={subirEsc} disabled={ocupado} style={{ display: 'none' }} />
                              </label>
                            )}
                        </div>
                      </div>
                    )}
                    {puedeEditar && (() => {
                      const faltanAprobar = faltantesParaAprobar(metodo, firmasDe(visible), !!visible.scan_path);
                      return (
                        <>
                          {faltanAprobar.length > 0 && <ul style={{ margin: 0, paddingLeft: 18, color: '#fbbf24', fontSize: '0.8rem' }}>{faltanAprobar.map(f => <li key={f}>{f}</li>)}</ul>}
                          <div style={S.botones}>
                            <button type="button" style={boton('primario', ocupado || faltanAprobar.length > 0)} disabled={ocupado || faltanAprobar.length > 0} onClick={aprobar}>Aprobar programa</button>
                            {firmasDe(visible).length === 0 && !visible.scan_path && <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={editar}>Volver a editar</button>}
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
                {visible.status === 'aprobada' && (
                  <div style={S.botones}><button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => pdfPrograma(visible, false)}>Descargar programa aprobado (PDF)</button></div>
                )}
              </div>

              {/* Ejecución */}
              {aprobada && (
                <div style={S.bloque}>
                  <h4 style={S.h3}>Ejecución · versión {aprobada.version} aprobada</h4>
                  <p style={S.nota}>{NOTA_PLAZOS}</p>
                  {actividadesAprobada.map(a => {
                    const r = mapaRespaldos.get(a.activity_key);
                    const est = estadoActividad(a.planned_date, r, hoyIso);
                    const e = (datos?.ejecuciones ?? []).find(x => x.activity_key === a.activity_key) ?? null;
                    return (
                      <div key={a.id} style={S.tarjeta}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ color: '#e2e8f0', fontSize: '0.84rem', fontWeight: 600, overflowWrap: 'anywhere', minWidth: 0 }}>{a.template_no ? `${a.template_no}. ` : ''}{a.title}</span>
                          <span style={{ color: COLOR_ACTIVIDAD[est], fontSize: '0.75rem', fontWeight: 700 }}>{ETIQUETA_ESTADO_ACTIVIDAD[est]}</span>
                        </div>
                        <span style={S.nota}>Planificada: {fechaCorta(a.planned_date)}{tipo === 'inspecciones' ? '' : ` · ${textoDestinatarios(a)}`}{r ? ` · Ejecutada: ${fechaCorta(r.fecha)}` : ''}</span>
                        {r && <span style={{ ...S.nota, color: '#cbd5e1', overflowWrap: 'anywhere' }}>Respaldo: {r.texto}</span>}
                        <div style={S.botones}>
                          {e?.evidence_path && <button type="button" style={boton('secundario')} onClick={() => abrirConUrl(urlArchivo(e.evidence_path!))}>Ver evidencia</button>}
                          {puedeEditar && ejecutando?.id !== a.id && (
                            <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => setEjecutando(a)}>{e ? 'Cambiar ejecución' : 'Registrar ejecución'}</button>
                          )}
                          {puedeEditar && e && (
                            <button type="button" style={boton('peligro', ocupado)} disabled={ocupado} onClick={() => {
                              if (window.confirm('¿Quitar la ejecución registrada? El archivo subido se conserva.')) ejecutar(() => borrarEjecucion(e), 'Ejecución quitada.');
                            }}>Quitar ejecución</button>
                          )}
                        </div>
                        {ejecutando?.id === a.id && programa && (
                          <FormEjecucion tipo={tipo} actividad={a} existente={e} origenes={origenes} ocupado={ocupado}
                            onCancelar={() => setEjecutando(null)}
                            onGuardar={x => ejecutar(async () => { await registrarEjecucion(programa, x, e); setEjecutando(null); }, 'Ejecución registrada.')} />
                        )}
                      </div>
                    );
                  })}
                  <div style={S.botones}><button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={pdfInforme}>Informe de ejecución (PDF)</button></div>
                </div>
              )}
            </>
          )}

          {/* Registro en el SUT */}
          {programa && (tipo === 'psicosocial' || tipo === 'sustancias') && (
            <div style={S.bloque}>
              <h4 style={S.h3}>Registro en el SUT del Ministerio del Trabajo ({cfg.itemSut?.replace('-', ' ')})</h4>
              {resultadoSutAnio && <p style={S.nota}><b style={{ color: COLOR_ESTADO[resultadoSutAnio.estado] }}>{ETIQUETA_ESTADO_ITEM[resultadoSutAnio.estado]}</b> · {resultadoSutAnio.resumen}</p>}
              {puedeEditar ? (
                <>
                  <label style={S.etiqueta}>Número o código del registro<input style={S.input} value={sut.codigo} maxLength={80} onChange={e => setSut(x => ({ ...x, codigo: e.target.value }))} /></label>
                  <label style={S.etiqueta}>Fecha del registro<input type="date" style={{ ...S.input, maxWidth: 200 }} value={sut.fecha} max={hoyIso} onChange={e => setSut(x => ({ ...x, fecha: e.target.value }))} /></label>
                  <div style={S.botones}>
                    <label style={{ ...boton('primario', ocupado), display: 'inline-flex', alignItems: 'center' }}>
                      {datos?.sut ? 'Reemplazar constancia y guardar' : 'Subir constancia y guardar'}
                      <input type="file" accept={ACEPTA_ARCHIVO} disabled={ocupado} style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0] ?? null; e.target.value = ''; if (f) guardarSutUI(f); }} />
                    </label>
                    {datos?.sut && <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => guardarSutUI(null)}>Guardar sin cambiar la constancia</button>}
                  </div>
                </>
              ) : datos?.sut && <p style={S.nota}>Código {datos.sut.registration_code} · {fechaCorta(datos.sut.registration_date)}</p>}
              {datos?.sut && <div style={S.botones}><button type="button" style={boton('secundario')} onClick={() => abrirConUrl(urlArchivo(datos.sut!.constancia_path))}>Ver constancia</button></div>}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ── Formulario de una actividad ──
function FormActividad({ tipo, valor, origenes, ocupado, onCambio, onGuardar, onCancelar }: {
  tipo: TipoPrograma; valor: ActividadEditable; origenes: Origenes; ocupado: boolean;
  onCambio: (a: ActividadEditable) => void; onGuardar: () => void; onCancelar: () => void;
}) {
  const set = (c: Partial<ActividadEditable>) => onCambio({ ...valor, ...c });
  return (
    <div style={{ ...S.tarjeta, borderColor: '#2FBF71' }}>
      <label style={S.etiqueta}>{tipo === 'examenes' ? 'Descripción' : tipo === 'inspecciones' ? 'Inspección (plantilla y área)' : 'Actividad o tema'}
        <input style={S.input} value={valor.title} maxLength={300} onChange={e => set({ title: e.target.value })} />
      </label>
      {tipo !== 'examenes' && tipo !== 'inspecciones' && (
        <label style={S.etiqueta}>Destinatarios
          <select style={S.input} value={valor.audience_kind} onChange={e => set({ audience_kind: e.target.value as Destinatarios })}>
            {(Object.keys(ETIQUETA_DESTINATARIOS) as Destinatarios[]).map(d => <option key={d} value={d}>{ETIQUETA_DESTINATARIOS[d]}</option>)}
          </select>
        </label>
      )}
      {valor.audience_kind === 'puesto' && (
        <label style={S.etiqueta}>Puesto
          <select style={S.input} value={valor.position_id ?? ''} onChange={e => set({ position_id: e.target.value || null })}>
            <option value="">Elija un puesto</option>
            {origenes.puestos.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      )}
      {valor.audience_kind === 'trabajador' && (
        <label style={S.etiqueta}>Trabajador
          <select style={S.input} value={valor.worker_id ?? ''} onChange={e => set({ worker_id: e.target.value || null })}>
            <option value="">Elija un trabajador</option>
            {origenes.trabajadores.filter(w => w.status !== 'desvinculado' || w.id === valor.worker_id).map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
          </select>
        </label>
      )}
      {tipo === 'examenes' && (
        <label style={S.etiqueta}>Tipo de examen
          <select style={S.input} value={valor.exam_type ?? 'periodico'} onChange={e => set({ exam_type: e.target.value as TipoExamen })}>
            {TIPOS_EXAMEN.map(t => <option key={t} value={t}>{ETIQUETA_EXAMEN[t]}</option>)}
          </select>
        </label>
      )}
      <label style={S.etiqueta}>Fecha planificada
        <input type="date" style={{ ...S.input, maxWidth: 200 }} value={valor.planned_date} onChange={e => set({ planned_date: e.target.value })} />
      </label>
      <label style={S.etiqueta}>Responsable
        <input style={S.input} value={valor.responsible ?? ''} maxLength={200} onChange={e => set({ responsible: e.target.value })} />
      </label>
      {tipo === 'sustancias' && (
        <label style={S.etiqueta}>Indicador (opcional)
          <input style={S.input} value={valor.indicator ?? ''} maxLength={300} placeholder={EJEMPLO_INDICADOR} onChange={e => set({ indicator: e.target.value || null })} />
        </label>
      )}
      {tipo !== 'examenes' && tipo !== 'inspecciones' && (
        <label style={S.etiqueta}>Duración (horas)
          <input type="number" min={0} max={1000} step={0.5} inputMode="decimal" style={{ ...S.input, maxWidth: 140 }} value={valor.duration_hours ?? ''}
            onChange={e => set({ duration_hours: e.target.value === '' ? null : Number(e.target.value) })} />
        </label>
      )}
      {tipo === 'capacitacion' && (
        <label style={S.etiqueta}>Capacitación vinculada (en Capacitaciones)
          <select style={S.input} value={valor.training_id ?? ''} onChange={e => set({ training_id: e.target.value || null })}>
            <option value="">Sin vincular</option>
            {origenes.capacitaciones.filter(c => c.status !== 'cancelada').map(c => <option key={c.id} value={c.id}>{c.title} · {fechaCorta(c.scheduled_date)}</option>)}
          </select>
        </label>
      )}
      <label style={S.etiqueta}>Notas
        <textarea style={{ ...S.input, minHeight: 50 }} value={valor.notes ?? ''} maxLength={1000} onChange={e => set({ notes: e.target.value })} />
      </label>
      <div style={S.botones}>
        <button type="button" style={boton('primario', ocupado)} disabled={ocupado} onClick={onGuardar}>Guardar actividad</button>
        <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  );
}

// ── Formulario de ejecución ──
function FormEjecucion({ tipo, actividad, existente, origenes, ocupado, onGuardar, onCancelar }: {
  tipo: TipoPrograma; actividad: FilaActividad; existente: FilaEjecucion | null; origenes: Origenes; ocupado: boolean;
  onGuardar: (e: Parameters<typeof registrarEjecucion>[1]) => void; onCancelar: () => void;
}) {
  const hoy = isoLocal(new Date());
  const [fecha, setFecha] = useState(existente?.executed_date ?? (actividad.planned_date < hoy ? actividad.planned_date : hoy));
  const [notas, setNotas] = useState(existente?.notes ?? '');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [vinculo, setVinculo] = useState(existente?.training_id ?? existente?.medical_exam_id ?? '');
  const [aviso, setAviso] = useState('');
  const opciones = tipo === 'capacitacion'
    ? origenes.capacitaciones.filter(c => c.status === 'realizada').map(c => ({ id: c.id, texto: textoCapacitacion(c), fecha: c.scheduled_date }))
    : tipo === 'examenes' && actividad.worker_id
      ? origenes.examenes.filter(x => x.worker_id === actividad.worker_id).map(x => ({ id: x.id, fecha: x.exam_date,
        texto: textoExamen(x, (actividad.audience_label ?? '').replace(/\s*\(.*\)$/, '').trim() || 'el trabajador') }))
      : [];
  const guardar = () => {
    setAviso('');
    if (!fecha || fecha > hoy) { setAviso('Indique la fecha real (no futura).'); return; }
    const op = opciones.find(o => o.id === vinculo);
    if (!archivo && !op && !existente?.evidence_path && !(existente?.linked_label ?? '').trim()) {
      setAviso('Suba la evidencia (archivo o foto) o vincule un registro que la respalde.'); return;
    }
    onGuardar({
      activity_key: actividad.activity_key, executed_date: fecha, notas, archivo,
      ...(op ? { training_id: tipo === 'capacitacion' ? op.id : null, medical_exam_id: tipo === 'examenes' ? op.id : null, linked_label: op.texto } : {}),
    });
  };
  return (
    <div style={{ ...S.tarjeta, borderColor: '#2FBF71' }}>
      <label style={S.etiqueta}>Fecha real de ejecución
        <input type="date" style={{ ...S.input, maxWidth: 200 }} value={fecha} max={hoy} onChange={e => setFecha(e.target.value)} />
      </label>
      {opciones.length > 0 && (
        <label style={S.etiqueta}>{tipo === 'capacitacion' ? 'Capacitación realizada que la respalda' : 'Examen hecho que la respalda'}
          <select style={S.input} value={vinculo} onChange={e => { setVinculo(e.target.value); const o = opciones.find(x => x.id === e.target.value); if (o) setFecha(o.fecha); }}>
            <option value="">Sin vincular</option>
            {opciones.map(o => <option key={o.id} value={o.id}>{o.texto}</option>)}
          </select>
        </label>
      )}
      <label style={S.etiqueta}>Evidencia (PDF, JPG o PNG, hasta 10 MB){existente?.evidence_name ? ` · actual: ${existente.evidence_name}` : ''}
        <input type="file" accept={ACEPTA_ARCHIVO} onChange={e => setArchivo(e.target.files?.[0] ?? null)} style={{ ...S.input, padding: 6 }} />
      </label>
      <label style={S.etiqueta}>Notas
        <textarea style={{ ...S.input, minHeight: 50 }} value={notas} maxLength={1000} onChange={e => setNotas(e.target.value)} />
      </label>
      {aviso && <p role="alert" style={{ ...S.nota, color: '#f87171' }}>{aviso}</p>}
      <div style={S.botones}>
        <button type="button" style={boton('primario', ocupado)} disabled={ocupado} onClick={guardar}>Guardar ejecución</button>
        <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  );
}
